<template>
    <transition name="cookie-banner">
        <div v-if="visible" class="cb-overlay">
            <div class="cb-banner">
                <!-- Header -->
                <div class="cb-header">
                    <div class="cb-icon">
                        <font-awesome-icon icon="fa-solid fa-cookie-bite" />
                    </div>
                    <div>
                        <h3 class="cb-title">Setări cookie-uri</h3>
                        <p class="cb-subtitle">Respectăm confidențialitatea datelor tale</p>
                    </div>
                </div>

                <!-- Description -->
                <p class="cb-description">
                    Folosim cookie-uri pentru a îmbunătăți experiența ta pe site.
                    Cookie-urile esențiale sunt necesare pentru funcționarea corectă a site-ului.
                    Cookie-urile opționale ne ajută să analizăm traficul și să personalizăm conținutul.
                    Poți alege ce categorii de cookie-uri accepți.
                </p>

                <!-- Preferences panel (expandable) -->
                <transition name="cb-expand">
                    <div v-if="showPreferences" class="cb-preferences">
                        <!-- Necessary -->
                        <div class="cb-category">
                            <div class="cb-category__info">
                                <span class="cb-category__name">Esențiale</span>
                                <span class="cb-category__desc">Necesare pentru funcționarea site-ului. Nu pot fi dezactivate.</span>
                            </div>
                            <div class="cb-category__toggle">
                                <input type="checkbox" checked disabled class="toggle toggle-sm toggle-success" />
                            </div>
                        </div>

                        <!-- Analytics -->
                        <div class="cb-category">
                            <div class="cb-category__info">
                                <span class="cb-category__name">Analitice</span>
                                <span class="cb-category__desc">Ne ajută să înțelegem cum este utilizat site-ul (Google Analytics).</span>
                            </div>
                            <div class="cb-category__toggle">
                                <input type="checkbox" v-model="consent.analytics" class="toggle toggle-sm toggle-success" />
                            </div>
                        </div>

                        <!-- Marketing -->
                        <div class="cb-category">
                            <div class="cb-category__info">
                                <span class="cb-category__name">Marketing</span>
                                <span class="cb-category__desc">Folosite pentru a afișa reclame relevante pe alte site-uri.</span>
                            </div>
                            <div class="cb-category__toggle">
                                <input type="checkbox" v-model="consent.marketing" class="toggle toggle-sm toggle-success" />
                            </div>
                        </div>
                    </div>
                </transition>

                <!-- Links -->
                <div class="cb-links">
                    <router-link to="/privacyPolicy" class="cb-link">Politica de confidențialitate</router-link>
                    <span class="cb-link-sep">·</span>
                    <router-link to="/cookiePolicy" class="cb-link">Politica de cookie-uri</router-link>
                </div>

                <!-- Actions -->
                <div class="cb-actions">
                    <button class="cb-btn cb-btn--outline" @click="rejectAll">
                        Refuză opționale
                    </button>
                    <button class="cb-btn cb-btn--ghost" @click="togglePreferences">
                        {{ showPreferences ? 'Ascunde opțiuni' : 'Personalizează' }}
                    </button>
                    <button v-if="showPreferences" class="cb-btn cb-btn--primary" @click="savePreferences">
                        Salvează preferințele
                    </button>
                    <button v-else class="cb-btn cb-btn--primary" @click="acceptAll">
                        Acceptă toate
                    </button>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import axios from 'axios';

export default {
    name: 'CookieNotification',
    data() {
        return {
            visible: false,
            showPreferences: false,
            isAuthenticated: false,
            consent: {
                necessary: true,
                analytics: false,
                marketing: false,
            },
        };
    },
    created() {
        this.isAuthenticated = !!localStorage.getItem('userToken');
        const stored = localStorage.getItem('cookieConsent');
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                this.consent = { ...this.consent, ...parsed.consent };
                if (parsed.consent.analytics) {
                    this.loadGoogleAnalytics();
                }
            } catch {
                this.visible = true;
            }
        } else {
            this.visible = true;
        }
    },
    methods: {
        acceptAll() {
            this.consent.analytics = true;
            this.consent.marketing = true;
            this.persistConsent();
            this.loadGoogleAnalytics();
            this.visible = false;
        },
        rejectAll() {
            this.consent.analytics = false;
            this.consent.marketing = false;
            this.persistConsent();
            this.disableGoogleAnalytics();
            this.visible = false;
        },
        savePreferences() {
            this.persistConsent();
            if (this.consent.analytics) {
                this.loadGoogleAnalytics();
            } else {
                this.disableGoogleAnalytics();
            }
            this.visible = false;
        },
        togglePreferences() {
            this.showPreferences = !this.showPreferences;
        },
        persistConsent() {
            const payload = {
                consent: { ...this.consent },
                timestamp: new Date().toISOString(),
                version: '1.0',
            };
            localStorage.setItem('cookieConsent', JSON.stringify(payload));

            // Backward compat: set old key so other code still works
            const legacyValue = this.consent.analytics ? 'accepted' : 'declined';
            localStorage.setItem('userCookieAccept', legacyValue);

            if (this.isAuthenticated) {
                this.updateConsentInDatabase(legacyValue);
            }
        },
        async updateConsentInDatabase(consentStatus) {
            try {
                const jwtToken = localStorage.getItem('userToken');
                await axios.post(
                    '/api/index.php?endpoint=updateConsent',
                    {
                        consent: consentStatus,
                    },
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${jwtToken}`,
                        },
                    }
                );
            } catch (error) {
                console.error(
                    'Failed to update consent in the database:',
                    error.response?.data || error.message
                );
            }
        },
        disableGoogleAnalytics() {
            // Set GA opt-out flag
            window['ga-disable-G-4GE00HXF05'] = true;

            // Remove GA cookies
            const cookiesToRemove = ['_ga', '_gid', '_gat'];
            cookiesToRemove.forEach((name) => {
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`;
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
            });
        },
        loadGoogleAnalytics() {
            const existingScript = document.querySelector(
                `script[src="https://www.googletagmanager.com/gtag/js?id=G-4GE00HXF05"]`
            );
            if (!existingScript) {
                const script = document.createElement('script');
                script.src =
                    'https://www.googletagmanager.com/gtag/js?id=G-4GE00HXF05';
                script.async = true;
                document.head.appendChild(script);

                script.onload = () => {
                    window.dataLayer = window.dataLayer || [];
                    function gtag() {
                        window.dataLayer.push(arguments);
                    }
                    window.gtag = gtag;

                    gtag('js', new Date());
                    gtag('config', 'G-4GE00HXF05', {
                        anonymize_ip: true,
                    });
                };
            }
        },
    },
};
</script>

<style scoped>
/* ── Banner transition ── */
.cookie-banner-enter-active,
.cookie-banner-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}
.cookie-banner-enter-from,
.cookie-banner-leave-to {
    opacity: 0;
    transform: translateY(20px);
}

/* ── Expand transition ── */
.cb-expand-enter-active,
.cb-expand-leave-active {
    transition: all 0.25s ease;
    overflow: hidden;
}
.cb-expand-enter-from,
.cb-expand-leave-to {
    opacity: 0;
    max-height: 0;
}
.cb-expand-enter-to,
.cb-expand-leave-from {
    opacity: 1;
    max-height: 400px;
}

/* ── Overlay ── */
.cb-overlay {
    position: fixed;
    inset: 0;
    z-index: 99998;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    background: rgba(0, 0, 0, 0.3);
    padding: 16px;
}

/* ── Banner card — force left alignment to override parent text-align: center ── */
.cb-banner {
    width: 100%;
    max-width: 480px;
    background: #fff;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    text-align: left;
}

/* ── Header ── */
.cb-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
}
.cb-icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: #fef3c7;
    color: #d97706;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 17px;
    flex-shrink: 0;
}
.cb-title {
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    line-height: 1.3;
    text-align: left;
}
.cb-subtitle {
    font-size: 11.5px;
    color: #64748b;
    margin: 2px 0 0;
    line-height: 1.3;
    text-align: left;
}

/* ── Description ── */
.cb-description {
    font-size: 12.5px;
    line-height: 1.55;
    color: #475569;
    margin: 0 0 14px;
    text-align: left;
}

/* ── Preferences ── */
.cb-preferences {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 14px;
}
.cb-category {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
}
.cb-category__info {
    flex: 1;
    min-width: 0;
    text-align: left;
}
.cb-category__name {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
    line-height: 1.3;
}
.cb-category__desc {
    display: block;
    font-size: 11px;
    color: #64748b;
    line-height: 1.35;
    margin-top: 1px;
}
.cb-category__toggle {
    flex-shrink: 0;
    display: flex;
    align-items: center;
}

/* ── Links ── */
.cb-links {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 14px;
}
.cb-link {
    font-size: 11.5px;
    color: #3b82f6;
    text-decoration: none;
    transition: color 0.15s ease;
}
.cb-link:hover {
    color: #2563eb;
    text-decoration: underline;
}
.cb-link-sep {
    color: #cbd5e1;
    font-size: 11px;
}

/* ── Actions ── */
.cb-actions {
    display: flex;
    gap: 8px;
}
.cb-btn {
    height: 36px;
    padding: 0 14px;
    border-radius: 8px;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    border: none;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}
.cb-btn--primary {
    background: #3b82f6;
    color: #fff;
    flex: 1;
}
.cb-btn--primary:hover {
    background: #2563eb;
}
.cb-btn--outline {
    background: #fff;
    color: #475569;
    border: 1px solid #cbd5e1;
}
.cb-btn--outline:hover {
    background: #f1f5f9;
    border-color: #94a3b8;
}
.cb-btn--ghost {
    background: transparent;
    color: #3b82f6;
    padding: 0 10px;
}
.cb-btn--ghost:hover {
    background: #eff6ff;
}

/* ── Mobile adjustments ── */
@media (max-width: 480px) {
    .cb-banner {
        padding: 20px 16px;
        border-radius: 14px;
    }
    .cb-actions {
        flex-direction: column;
    }
    .cb-btn {
        width: 100%;
        height: 40px;
        font-size: 13px;
    }
    .cb-btn--ghost {
        order: 3;
    }

    .cb-btn--primary {
        flex: none;
    }
}
</style>
